<script lang="ts">
	import { Checkbox, CheckboxSize, Icon } from '@repo/commons/components'
	import { t } from 'svelte-i18n'
	import { SortOrder } from './Table.svelte'
	import SortAction from './SortAction.svelte'

	type Props = {
		columns: string[]
		columnStyles?: Record<string, string>
		enableRowSelect?: boolean
		selectedRowsSet?: Set<any>
		handleCheckboxChange: (checked: boolean, id: number) => void
		onUpdateSorting: (updatedRows: any[]) => void
		rows: any[]
		showFilters?: boolean
		enableSorting?: boolean
	}
	const {
		columns,
		columnStyles = {},
		enableRowSelect = false,
		selectedRowsSet = new Set<any>(),
		showFilters = false,
		enableSorting = false,
		rows = [],
		onUpdateSorting,
		handleCheckboxChange
	}: Props = $props()

	const allRowsSelected = $derived.by(() => {
		if (rows.length === 0) return false
		return rows.every((row) => selectedRowsSet.has(row.id))
	})

	const someRowsSelected = $derived.by(() => {
		const selectedCount = rows.filter((row) => selectedRowsSet.has(row.id)).length
		return selectedCount > 0 && selectedCount < rows.length
	})

	let sortColumn: string | null = $state(null)
	let sortOrder: SortOrder | null = $state(null)

	const sortRows = (column: string, requestedOrder?: SortOrder): void => {
		if (sortColumn === column) {
			if (requestedOrder) {
				sortOrder = requestedOrder
			} else {
				sortOrder = sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC
			}
		} else {
			sortColumn = column
			sortOrder = requestedOrder || SortOrder.ASC
		}

		const sortedRows = [...rows].sort((a: any, b: any): number => {
			// Check for null values first
			if (a[column] === null && b[column] === null) return 0
			if (a[column] === null) return 1 // nulls are considered greater, so they appear last
			if (b[column] === null) return -1

			// If values are Date or representable as Date and not null, attempt to compare as dates
			let dateA: Date = new Date(a[column])
			let dateB: Date = new Date(b[column])
			if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
				if (dateA < dateB) return sortOrder === SortOrder.ASC ? -1 : 1
				if (dateA > dateB) return sortOrder === SortOrder.ASC ? 1 : -1
			} else {
				// Fallback to string comparison if not dealing with valid dates
				let strA: string = String(a[column])
				let strB: string = String(b[column])
				if (strA < strB) return sortOrder === SortOrder.ASC ? -1 : 1
				if (strA > strB) return sortOrder === SortOrder.ASC ? 1 : -1
			}
			return 0
		})
		onUpdateSorting(sortedRows)
	}
</script>

{#if enableRowSelect}
	<div class="check-all-cell box">
		<Checkbox
			checked={allRowsSelected}
			indeterminate={someRowsSelected}
			onchange={(checked) => handleCheckboxChange(checked, -1)}
			size={CheckboxSize.SMALL}
			labelExternalStyle="margin-bottom: unset;"
		/>
	</div>
{/if}
{#each columns as column, index (column)}
	<div
		class="tab-title box"
		class:last-index={index == columns.length - 1}
		style={columnStyles[column] ?? columnStyles['*']}
	>
		<div class="sortable-header">
			{$t(column)}
			{#if showFilters || enableSorting}
				<div class="icon-container">
					{#if enableSorting && column !== 'actions'}
						<SortAction {column} {sortColumn} {sortOrder} onSort={sortRows} />
					{/if}

					{#if showFilters}
						<Icon name="filter" size="sm" class="text-[#002C4D] opacity-50" />
					{/if}
				</div>
			{/if}
		</div>
	</div>
{/each}

<style lang="postcss">
	.check-all-cell.box,
	.tab-title.box {
		color: #000;
		padding: 10px;
		font-size: 150%;
		font-size: 12px;
		border-bottom: 1px solid #e4e7ed;
	}

	.tab-title {
		@apply text-[#001d33]/70 text-sm font-semibold capitalize leading-tight whitespace-nowrap w-auto;
	}

	.sortable-header {
		@apply cursor-pointer flex items-center text-sm font-semibold text-[#001D33B2] leading-5;
	}

	.icon-container {
		@apply flex flex-row items-center justify-center;
	}

	.check-all-cell {
		padding: 12px 24px 12px 4px;
	}

	:global(.selection-checkbox-column) {
		min-width: 20px;
		width: 24px !important;
		height: 24px !important;
	}

	.tab-title.box.last-index .sortable-header {
		justify-content: flex-end;
	}
</style>
